import type { ReactNode } from "react";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useHasMounted } from "utils/wrappers";
import {
  drawerSizeStyle,
  drawerStyle,
  overlayHiddenStyle,
  overlayPositionOff,
  overlayPositionOn,
  overlayStyle
} from "./OverlayDrawer.css";

export const OverlayDrawer: React.FC<{
  children: ReactNode;
  anchorPosition?: "default" | "top" | "bottom";
  className?: string;
  handleDismiss?: () => void;
  isOpen?: boolean;
  size?: "default" | "fill";
  ref?: React.Ref<HTMLDivElement>;
}> = ({
  children,
  anchorPosition = "default",
  size = "default",
  handleDismiss,
  isOpen,
  className,
  ref
}) => {
  const mounted = useHasMounted();

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target as HTMLElement;
      if (
        (event instanceof MouseEvent &&
          target !== target.closest("[role='dialog']") &&
          !target.closest("[role='dialog']") &&
          target.closest("#overlay_portal")) ||
        (event instanceof KeyboardEvent && event.key === "Escape")
      ) {
        handleDismiss?.();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleClickOutside);
      document.addEventListener("click", handleClickOutside);
    } else {
      window.removeEventListener("keydown", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      window.removeEventListener("keydown", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen, handleDismiss]);

  // should not render this component clint side until it's mounted to avoid hydration issues.
  if (!mounted) return null;

  const overlayPortal =
    typeof document !== "undefined" &&
    document.getElementById("overlay_portal");

  if (!overlayPortal) return null;

  return createPortal(
    <div
      ref={ref}
      role="presentation"
      className={`${isOpen ? overlayStyle : overlayHiddenStyle}`}
      tabIndex={-1}
    >
      <div
        role="dialog"
        data-testid="overlay-container"
        className={`${isOpen ? overlayPositionOn : overlayPositionOff} ${
          drawerStyle[anchorPosition]
        } ${drawerSizeStyle[size]} ${className}`}
      >
        {children}
      </div>
    </div>,
    overlayPortal
  );
};
